import { DocumentData, QueryDocumentSnapshot, updateDoc } from "firebase/firestore";
import { callApi, RequestType } from "../fetch";
import { showToast } from "../toast";

// Once a person's company has switched over to the new booking service, the Firestore rules refuse
// OnePark's direct edits to their account. OnePark can't see which companies have switched, so it writes
// directly as it always has and, only when the rules refuse, sends the same change through the service.
// People at companies that haven't switched cost nothing extra.
export const refusedByRules = (error: unknown): boolean => (error as { code?: string } | null)?.code === "permission-denied";

// A person's settings couldn't be saved, and the admin has already been told whose. Callers let it
// propagate, so the rule save stops instead of reporting success.
export class PersonSaveError extends Error {
    name = "PersonSaveError";
}
// By name, not instanceof: transpiled builds can lose an Error subclass's prototype.
export const isPersonSaveError = (error: unknown): boolean => (error as Error | null)?.name === "PersonSaveError";

// Sets a rule's per-person values (VIP parking, weekend, booking limit and so on) on one person's account.
// Throws PersonSaveError, after telling the admin, when the service refuses too.
export const savePersonSettings = async (userDoc: QueryDocumentSnapshot<DocumentData>, changes: Record<string, unknown>): Promise<void> => {
    try {
        await updateDoc(userDoc.ref, { ...userDoc.data(), ...changes });
        return;
    } catch (error) {
        if (!refusedByRules(error)) throw error;
    }
    const response = await callApi("bookingApiV2/users/update", { userId: userDoc.id, changes }, RequestType.POST, { signedIn: true });
    if (!response.error) return;
    const message = `We couldn't update ${userDoc.data().name || "this person"}'s settings just now, so the rule wasn't fully saved. Please try again.`;
    showToast(message, { type: "error" });
    throw new PersonSaveError(message);
};

const REFRESH_REFUSALS: Record<string, string> = {
    no_card: "This person doesn't have an access card yet, so there's nothing to refresh.",
    card_not_recognised: "The car park system didn't recognise this person's card. Please check it and try again.",
};

// Refreshes a switched-over company's person from the car park system: the booking service looks up their
// card and saves their company, card and plates itself.
export const refreshPersonViaService = async (userId: string): Promise<void> => {
    const response = await callApi("bookingApiV2/profile/refresh", { userId }, RequestType.POST, { signedIn: true });
    if (!response.error) return;
    const message = (response.code && REFRESH_REFUSALS[response.code]) || "We couldn't refresh this person just now. Please try again.";
    showToast(message, { type: "error" });
    throw new Error(message);
};
